import axios from 'axios';
import {isRef, nextTick, ref, unref, watchEffect} from 'vue';
import TokenService from "@/services/token.service";
import {useComponent} from "@/utils/compossable/$component";
import router from "@/GenerationRouter"
import {useMemberStore} from "@/services/store/memberStore";
import dayjs from "dayjs";

// axios.defaults.baseURL = process.env.VUE_APP_API_URL;
//axios.defaults.headers.common['X-Authorization'] = 'Bearer AccessToken';
axios.defaults.headers.post['Content-Type'] = 'application/json';

const defaultConfig = {
    method: 'post'
};

const defaultOptions = {
    immediate: false,
    exceptionThrow: false,
    loading: false,
};

let interceptors = []

axios.interceptors.request.use(
    (config) => {
        const token = JSON.parse(sessionStorage.getItem("token"))?.accToken ?? false;
        token ? config.headers['Authorization'] = `Bearer ${token}` : void (0);
        if (token) {
            config.headers['actorId'] = JSON.parse(sessionStorage.getItem("token"))?.actorId ?? null; // vizend에서 필수로 받는 actorId - 로그인 시 전달 받음
        }
        let SERVER_URL = window.location.origin;
        /* 중계요청으로 인한 header 추가정보 */
        config.headers['X-Trail-Scene-url'] = SERVER_URL + config.url;
        config.headers['X-Trail-Actor-Id'] = JSON.parse(sessionStorage.getItem("token"))?.actorId ?? null;
        config.headers['X-Trail-Cs-Time'] = dayjs().unix();
        config.headers['X-Trail-Location'] = 'Seoul/Korea';

        if (interceptors.length <= 0) {
            return config;
        }

        interceptors.forEach(obj => {
            Object.entries(obj).forEach(([key, value]) => {
                if (config.hasOwnProperty(key)) {
                    config[key] = value;
                }
            });
        });

        interceptors = [];

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axios.interceptors.response.use(
    function (response) {
        return response;
    },
    async (error) => {
        const $component = useComponent();
        const alertCall = (message) => {
            setTimeout(() => {
                $component.simpleAlert(`${message}\n로그인 페이지로 이동합니다`);
                $component.alert.fn.fnSelect = async () => {
                    const store = useMemberStore();
                    $component.delTimer();
                    await sessionStorage.clear();
                    await store.init();
                    await nextTick(async () => {
                        await router.instance.push('/claim/web/login/loginCertificate/3');
                    });
                }
                $component.alert.fn.fnClose = async () => {
                    const store = useMemberStore();
                    $component.delTimer();
                    await sessionStorage.clear();
                    await store.init();
                    await nextTick(async () => {
                        await router.instance.push('/claim/web/login/loginCertificate/3');
                    });
                }
            },550)
        }

        const {config, response: {status}} = error;

        if (status === 401 || status === 404) {
            if (error.response.data.failureMessage.exceptionMessage === "JWT Validate Error" ||
                error.response.data.failureMessage.exceptionName === "TOKEN_ACCESS_EXPIRED" ||
                error.response.data.failureMessage.exceptionName === "TOKEN_ACCESS_MISMATCH") {
                const originalRequest = config;
                const refreshToken = JSON.parse(sessionStorage.getItem("token"))?.refToken ?? false;
                if (refreshToken) {
                    const response = await axios.post('/cmm/api/v1/member/getToken', {refToken: refreshToken},
                        {headers: {authorization: `Bearer ${refreshToken}`}});
                    const {accToken: newAccessToken, refToken: newRefreshToken} = response.data.result;
                    TokenService.updateLocalAccessToken(newAccessToken);
                    TokenService.updateLocalRefreshToken(newRefreshToken);

                    originalRequest.headers.authorization = `Bearer ${newAccessToken}`;
                    return axios(originalRequest);

                } else {
                    alertCall('토큰정보가 존재하지 않습니다.');
                }
            } else if (error.response.data.failureMessage.exceptionName === "TOKEN_REFRESH_EXPIRED" ||
                error.response.data.failureMessage.exceptionName === "TOKEN_REFRESH_MISMATCH") {

                alertCall(error.response.data.failureMessage.exceptionMessage);
            } else if (error.response.data.failureMessage.exceptionName === "LOGIN_ID_NOT_EXIST") {
                alertCall(error.response.data.failureMessage.exceptionMessage);
            }
        } else if (status === 500) {
            if (error.response.data?.failureMessage?.exceptionName === "ServletException") {
                alertCall('토큰 검증이 불가능 합니다.')
            }
        }

        return Promise.reject(error);
    }
);

export const useAxios = async (url, config = {}, options = {}) => {
    const $component = useComponent();
    const response = ref(null);
    const data = ref(null);
    const error = ref(null);

    const {onSuccess, onError, immediate, exceptionThrow, loading} = {
        ...defaultOptions,
        ...options,
    };

    const {params} = config;
    const execute = async body => {
        data.value = null;
        error.value = null;
        if (loading) {
            $component.loading.enable = true;
        }
        try {
            const res = await axios(unref(url), {
                ...defaultConfig,
                ...config,
                params: unref(params),
                data: body ?? {},
            })

            response.value = res;
            data.value = res.data;

            if (onSuccess) {
                onSuccess(res);
            }
        } catch (err) {
            error.value = err;

            if (onError) {
                onError(err);
            } else {
                if (exceptionThrow) {
                    throw err;
                }
            }
        } finally {
            $component.loading.enable = false
        }
    };

    if (isRef(params) || isRef(url)) {
        watchEffect(await execute);
    } else {
        if (immediate) {
            await execute();
        }
    }

    return {
        response,
        data,
        error,
        loading,
        execute,
    }
};
